import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import {
  Close,
  People,
  NetworkCheck,
  Refresh,
  AccessTime,
  Computer,
  Router as RouterIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import routerServiceV6 from '../services/routerServiceV6';
import routerServiceV7 from '../services/routerServiceV7';
import { PPPConnection, PPPConnectionsData } from '../types';

interface PPPConnectionsDialogProps {
  open: boolean;
  onClose: () => void;
  router: any;
}

const PPPConnectionsDialog: React.FC<PPPConnectionsDialogProps> = ({ open, onClose, router }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pppData, setPppData] = useState<PPPConnectionsData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Helper function to format bytes
  const formatBytes = (bytes: string | number): string => {
    if (!bytes || bytes === '0') return '0 B';
    const bytesNum = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (isNaN(bytesNum)) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytesNum) / Math.log(k));
    
    return parseFloat((bytesNum / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fetch PPP connections from API
  const fetchPPPConnections = async () => {
    if (!router?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching PPP connections for:', router.id);
      const service = router.os_version === '7' ? routerServiceV7 : routerServiceV6;
      const response = await service.getPPPActiveConnections(router.id);
      
      console.log('PPP connections response:', response);
      
      if (response.data && response.data.success && response.data.data) {
        setPppData(response.data.data);
        setLastUpdated(new Date());
      } else {
        setError('Data PPP connections tidak valid');
      }
    } catch (err: any) {
      console.error('Error fetching PPP connections:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch PPP connections');
    } finally {
      setLoading(false);
    }
  };

  // Load data when dialog opens
  useEffect(() => {
    if (open && router?.id) {
      fetchPPPConnections();
    }
  }, [open, router?.id]);

  // Auto refresh disabled to prevent API flooding
  // useEffect(() => {
  //   if (!open || !pppData) return;

  //   const interval = setInterval(() => {
  //     fetchPPPConnections();
  //   }, 10000); // Update every 10 seconds

  //   return () => clearInterval(interval);
  // }, [open, pppData, router?.id]);

  const getServiceIcon = (service: string) => {
    switch (service?.toLowerCase()) {
      case 'pppoe':
        return <NetworkCheck color="primary" />;
      case 'pptp':
        return <Computer color="secondary" />;
      case 'l2tp':
        return <RouterIcon color="info" />;
      case 'sstp':
        return <NetworkCheck color="success" />;
      default:
        return <NetworkCheck />;
    }
  };

  const getServiceColor = (service: string) => {
    switch (service?.toLowerCase()) {
      case 'pppoe':
        return 'primary';
      case 'pptp':
        return 'secondary';
      case 'l2tp':
        return 'info';
      case 'sstp':
        return 'success';
      default:
        return 'default';
    }
  };

  if (!router) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, height: '90vh' }
      }}
    >
      <DialogTitle sx={{ position: 'relative', pb: 2 }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
        >
          <Close />
        </IconButton>
        
        <Box display="flex" alignItems="center" gap={2} pt={1}>
          <People color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              PPP Active Connections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {router.name} - {router.host || router.ip_address}:{router.port}
            </Typography>
          </Box>
          <Chip 
            label={`Last Updated: ${format(lastUpdated, 'HH:mm:ss')}`} 
            color="info"
            icon={<AccessTime />}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6">Connection Error</Typography>
            <Typography variant="body2">{error}</Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={fetchPPPConnections}
              sx={{ mt: 2 }}
              startIcon={<Refresh />}
            >
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && pppData && (
          <>
            {/* Summary Cards */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
              <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'primary.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <People sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {pppData.summary?.totalConnections || 0}
                  </Typography>
                  <Typography variant="subtitle2">Total Connections</Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'success.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <NetworkCheck sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {pppData.summary?.pppoeConnections || 0}
                  </Typography>
                  <Typography variant="subtitle2">PPPoE</Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'info.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Computer sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" color="info.main" fontWeight="bold">
                    {pppData.summary?.pptpConnections || 0}
                  </Typography>
                  <Typography variant="subtitle2">PPTP</Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'warning.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <RouterIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {(pppData.summary?.l2tpConnections || 0) + (pppData.summary?.sstpConnections || 0)}
                  </Typography>
                  <Typography variant="subtitle2">L2TP/SSTP</Typography>
                </CardContent>
              </Card>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Connections Table */}
            <Typography variant="h6" color="primary" gutterBottom>
              Active Connections Details
            </Typography>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400, overflowX: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name / User (KLBT)</strong></TableCell>
                    <TableCell><strong>Service</strong></TableCell>
                    <TableCell><strong>IP Address</strong></TableCell>
                    <TableCell><strong>Uptime</strong></TableCell>
                    <TableCell><strong>Session Info</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pppData.connections && pppData.connections.length > 0 ? (
                    pppData.connections.map((connection: PPPConnection, index: number) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getServiceIcon(connection.service)}
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {connection.name || connection.userName || 'N/A'}
                              </Typography>
                            </Box>
                            {connection.clientName && (
                              <Typography variant="caption" color="text.secondary">
                                Client: {connection.clientName}
                              </Typography>
                            )}
                            {connection.comment && (
                              <Typography variant="caption" color="text.secondary">
                                Comment: {connection.comment}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={connection.service?.toUpperCase() || 'N/A'} 
                            size="small" 
                            color={getServiceColor(connection.service) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                            {connection.address || 'N/A'}
                          </Typography>
                          {connection.remoteAddress && connection.remoteAddress !== connection.address && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Remote: {connection.remoteAddress}
                            </Typography>
                          )}
                          {connection.localAddress && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Local: {connection.localAddress}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {connection.uptime || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.75rem">
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <Box>ID: {connection.sessionId || 'N/A'}</Box>
                              <Box>Enc: {connection.encoding || 'N/A'}</Box>
                              {connection.command && (
                                <Box>Cmd: {connection.command}</Box>
                              )}
                            </Box>
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Chip 
                              label={connection.disabled ? 'Disabled' : 'Active'} 
                              size="small" 
                              color={connection.disabled ? 'error' : 'success'}
                            />
                            <Chip 
                              label={connection.radius ? 'RADIUS' : 'Local'} 
                              size="small" 
                              color={connection.radius ? 'info' : 'default'}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No active PPP connections found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button variant="outlined" onClick={onClose} size="large">
          Close
        </Button>
        <Button 
          variant="contained" 
          size="large"
          onClick={fetchPPPConnections}
          disabled={loading}
          startIcon={<Refresh />}
        >
          Refresh Data
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PPPConnectionsDialog;
